import React from "react";
import { Box, Typography, Container, Grid, keyframes } from "@mui/material";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { fadeInUp, staggerContainer } from "../utils/animations";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
// Import icon images
import GlucoseIcon from "../../introComponent/assets/icons/6192150.png";
import HemoglobinIcon from "../../introComponent/assets/icons/280e1057-61b9-432a-abf2-35b9025a7350.png";
import WBCIcon from "../../introComponent/assets/icons/e535b285-f349-4042-8c68-44452923cbef.png";

const smartFeatures = [
  "Clean visual interpretation & AI insights",
  "Empowered patients to take proactive steps",
  "Automated, instant reporting",
  "Personalized guidance & education",
];

const traditionalFeatures = [
  "Confusing numbers & medical jargon",
  "Patients unsure to understand results",
  "Manual workflows & delays",
  "Limited engagement",
];

// ============ ANIMATIONS ============
const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

// ============ SAMPLE TEST DATA ============
const sampleTestData = [
  {
    id: 'glucose',
    name: "Glucose",
    value: 92,
    unit: " mg/dL",
    min: 50,
    max: 150,
    normalMin: 70,
    normalMax: 100,
    insight: "Your glucose level is optimal",
    iconPath: GlucoseIcon,
  },
  {
    id: 'hemoglobin',
    name: "Hemoglobin",
    value: 14.2,
    unit: " g/dL",
    min: 8,
    max: 18,
    normalMin: 12,
    normalMax: 16,
    insight: "Healthy oxygen capacity",
    iconPath: HemoglobinIcon,
  },
  {
    id: 'wbc',
    name: "WBC Count",
    value: 6.8,
    unit: " ×10³/μL",
    min: 2.0,
    max: 15.0,
    normalMin: 4.0,
    normalMax: 11.0,
    insight: "Normal immune response",
    iconPath: WBCIcon,
  },
];

// ============ COMPACT RANGE BAR ============
const CompactRangeBar = ({ value, min, max, normalMin, normalMax, unit }) => {
  const totalRange = max - min;
  const normalStartPercent = ((normalMin - min) / totalRange) * 100;
  const normalWidthPercent = ((normalMax - normalMin) / totalRange) * 100;
  const valuePercent = Math.min(Math.max(((value - min) / totalRange) * 100, 2), 98);
  
  const isNormal = value >= normalMin && value <= normalMax;
  const markerColor = isNormal ? "#10b981" : "#ef4444";

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ position: "relative", height: "24px", display: "flex", alignItems: "flex-end", marginBottom: "2px" }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "6px",
            borderRadius: "3px",
            overflow: "visible",
          }}
        >
          {/* Low Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: "0%",
              width: `${normalStartPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #ef4444, #dc2626)",
              borderTopLeftRadius: "3px",
              borderBottomLeftRadius: "3px",
            }}
          />
          {/* Normal Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent}%`,
              width: `${normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #10b981, #059669)",
            }}
          />
          {/* High Range Zone */}
          <Box
            sx={{
              position: "absolute",
              left: `${normalStartPercent + normalWidthPercent}%`,
              width: `${100 - normalStartPercent - normalWidthPercent}%`,
              height: "100%",
              background: "linear-gradient(90deg, #ef4444, #dc2626)",
              borderTopRightRadius: "3px",
              borderBottomRightRadius: "3px",
            }}
          />
          {/* Value Marker */}
          <Box
            sx={{
              position: "absolute",
              left: `${valuePercent}%`,
              transform: "translateX(-50%)",
              bottom: "100%",
              marginBottom: "3px",
            }}
          >
            <Box
              sx={{
                background: markerColor,
                color: "#fff",
                padding: "1px 4px",
                borderRadius: "3px",
                fontSize: "8px",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 700,
                whiteSpace: "nowrap",
                boxShadow: `0 1px 4px ${markerColor}40`,
              }}
            >
              {value}{unit}
            </Box>
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: "3px solid transparent",
                borderRight: "3px solid transparent",
                borderTop: `4px solid ${markerColor}`,
                margin: "0 auto",
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "2px" }}>
        <Typography sx={{ fontSize: "7px", color: "#64748b", fontFamily: "'Inter', sans-serif" }}>{min}</Typography>
        <Typography sx={{ fontSize: "7px", color: "#10b981", fontFamily: "'Inter', sans-serif", fontWeight: 600 }}>
          {normalMin}-{normalMax}
        </Typography>
        <Typography sx={{ fontSize: "7px", color: "#64748b", fontFamily: "'Inter', sans-serif" }}>{max}</Typography>
      </Box>
    </Box>
  );
};

// ============ SMART REPORT PREVIEW COMPONENT ============
const SmartReportPreview = ({ testData }) => {
  const normalCount = testData.filter(item => item.value >= item.normalMin && item.value <= item.normalMax).length;
  
  return (
    <Box
      sx={{
        width: "100%",
        background: "#ffffff",
        borderRadius: "12px",
        padding: { xs: "8px", sm: "10px" },
        border: "1px solid rgba(0, 0, 0, 0.1)",
        position: "relative",
        overflow: "hidden",
        animation: `${slideInRight} 0.6s ease-out`,
        minHeight: "auto",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
      }}
    >
      {/* Recommended Badge */}
      <Box
        sx={{
          position: "absolute",
          top: "8px",
          right: "8px",
          background: "linear-gradient(135deg, #10b981, #059669)",
          borderRadius: "12px",
          padding: "2px 8px",
          boxShadow: "0 2px 8px rgba(16, 185, 129, 0.4)",
        }}
      >
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "8px", fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          ✨ Smart
        </Typography>
      </Box>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px", marginTop: "2px" }}>
        <Box
          sx={{
            width: "24px",
            height: "24px",
            borderRadius: "8px",
            background: "rgba(53, 103, 214, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(53, 103, 214, 0.2)",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: "14px", color: "#3567D6" }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "12px", fontWeight: 700, color: "#1D47A4" }}>
            Smart Report
          </Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", color: "#64748b", fontWeight: 500 }}>
            Visual insights
          </Typography>
        </Box>
      </Box>

      {/* Patient Details */}
      <Box sx={{ marginBottom: "8px", padding: "6px 8px", background: "rgba(53, 103, 214, 0.05)", borderRadius: "6px", border: "1px solid rgba(53, 103, 214, 0.15)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
          <PersonIcon sx={{ fontSize: "10px", color: "#3567D6" }} />
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "8px", color: "#1D47A4", fontWeight: 600 }}>
            Patient Details
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "8px", color: "#475569", fontWeight: 500 }}>
            Name: <Box component="span" sx={{ fontWeight: 600, color: "#1D47A4" }}>John Doe</Box>
          </Typography>
          <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "8px", color: "#475569", fontWeight: 500 }}>
            Age: <Box component="span" sx={{ fontWeight: 600, color: "#1D47A4" }}>35</Box>
          </Typography>
        </Box>
      </Box>

      {/* Status Summary */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
          gap: "8px",
        }}
      >
        <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", fontWeight: 600, color: "#1D47A4" }}>
          Status
        </Typography>
        {normalCount > 0 && (
          <Box
            sx={{
              background: "rgba(16, 185, 129, 0.4)",
              borderRadius: "4px",
              padding: "2px 6px",
              display: "flex",
              alignItems: "center",
              gap: "2px",
              border: "1px solid rgba(16, 185, 129, 0.5)",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: "10px", color: "#10b981" }} />
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "8px", fontWeight: 600, color: "#10b981" }}>
              {normalCount}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Parameters - Show only 2 for compactness */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {testData.slice(0, 2).map((item, idx) => {
          const isNormal = item.value >= item.normalMin && item.value <= item.normalMax;
          return (
            <Box
              key={idx}
              sx={{
                background: isNormal 
                  ? "rgba(16, 185, 129, 0.2)" 
                  : "rgba(239, 68, 68, 0.2)",
                border: `1px solid ${isNormal ? "rgba(16, 185, 129, 0.4)" : "rgba(239, 68, 68, 0.4)"}`,
                borderRadius: "8px",
                padding: "6px",
                transition: "all 0.3s ease",
              }}
            >
              {/* Parameter Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flex: 1 }}>
                  {item.iconPath && (
                    <Box
                      sx={{
                        width: "16px",
                        height: "16px",
                        borderRadius: "3px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.iconPath}
                        alt={item.name}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </Box>
                  )}
                  <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "9px", fontWeight: 600, color: "#1D47A4" }}>
                    {item.name}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background: isNormal 
                      ? "rgba(16, 185, 129, 0.4)" 
                      : "rgba(239, 68, 68, 0.4)",
                    border: `1px solid ${isNormal ? "rgba(16, 185, 129, 0.6)" : "rgba(239, 68, 68, 0.6)"}`,
                    color: isNormal ? "#10b981" : "#ef4444",
                    padding: "1px 4px",
                    borderRadius: "3px",
                    fontSize: "8px",
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {isNormal ? "✓" : "⚠"}
                </Box>
              </Box>

              {/* Range Bar - Smaller */}
              <Box sx={{ marginTop: "4px" }}>
                <CompactRangeBar
                  value={item.value}
                  min={item.min}
                  max={item.max}
                  normalMin={item.normalMin}
                  normalMax={item.normalMax}
                  unit={item.unit}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const ComparisonSection = () => {
  return (
    <Box component="section" sx={{ py: 0 }}>
      <Box
        sx={{
          backgroundColor: "#E8F2FF",
          py: { xs: 6, sm: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
            sx={{ textAlign: "center", mb: { xs: 4, sm: 5, md: 6 } }}
          >
            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "rgba(53, 103, 214, 0.1)",
                color: "#3567D6",
                px: { xs: 2.5, sm: 3 },
                py: { xs: 0.75, sm: 1 },
                borderRadius: "9999px",
                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                fontWeight: 600,
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Health Report File
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "28px", sm: "36px", md: "48px" },
                fontWeight: 700,
                color: "#1D47A4",
                fontFamily: "'Poppins', sans-serif",
                lineHeight: 1.2,
                px: { xs: 1, sm: 0 },
              }}
            >
              How NirogGyan Transforms
              <br />
              <Box component="span" sx={{ color: "#3567D6" }}>
                Healthcare Reporting
              </Box>
            </Typography>
          </Box>

          <Grid
            container
            spacing={{ xs: 3, sm: 4 }}
            sx={{ maxWidth: "80rem", mx: "auto" }}
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {/* Smart Reporting Card */}
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                variants={fadeInUp}
                sx={{
                  background: "linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)",
                  borderRadius: { xs: "20px", md: "24px" },
                  p: { xs: 3, sm: 4, md: 5 },
                  color: "#ffffff",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 10px 40px rgba(37, 99, 235, 0.25)",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: "22px", sm: "24px", md: "28px" },
                    fontWeight: 700,
                    mb: { xs: 3, md: 4 },
                    fontFamily: "'Poppins', sans-serif",
                    lineHeight: 1.2,
                    color: "#ffffff",
                  }}
                >
                  Smart Reporting with
                  <br />
                  NirogGyan
                </Typography>

                {/* Smart Report Preview */}
                <Box sx={{ mb: { xs: 3, md: 4 } }}>
                  <SmartReportPreview testData={sampleTestData} />
                </Box>

                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: "auto" }}>
                  {smartFeatures.map((feature, index) => (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: { xs: 1, sm: 1.5 },
                        mb: { xs: 1.25, sm: 1.5 },
                      }}
                    >
                      <Check
                        size={18}
                        style={{
                          color: "#10b981",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: { xs: "13px", sm: "14px", md: "15px" },
                          fontFamily: "'Poppins', sans-serif",
                          lineHeight: 1.5,
                          color: "#ffffff",
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Traditional Reports Card */}
            <Grid item xs={12} md={6}>
              <Box
                component={motion.div}
                variants={fadeInUp}
                sx={{
                  background: "linear-gradient(145deg, #F5F2F0 0%, #E8E3DF 100%)",
                  borderRadius: { xs: "20px", md: "24px" },
                  p: { xs: 3, sm: 4, md: 5 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { xs: "22px", sm: "24px", md: "28px" },
                    fontWeight: 700,
                    color: "#1D47A4",
                    mb: { xs: 3, md: 4 },
                    fontFamily: "'Poppins', sans-serif",
                    lineHeight: 1.2,
                  }}
                >
                  Traditional
                  <br />
                  Reports
                </Typography>

                {/* Code Snippet */}
                <Box
                  sx={{
                    backgroundColor: "#ffffff",
                    borderRadius: { xs: "10px", md: "12px" },
                    p: { xs: 2, sm: 2.5 },
                    mb: { xs: 3, md: 3 },
                    fontFamily: "'Courier New', monospace",
                    fontSize: { xs: "10px", sm: "11px", md: "12px" },
                    overflow: "auto",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    flex: 1,
                    minHeight: 0,
                  }}
                >
                  <Box component="pre" sx={{ m: 0, color: "#4C4C4C", lineHeight: 1.6 }}>
{`PATIENT: DOE, JOHN
DATE: 01/15/2024

TEST RESULTS:
---------------------------
GLUCOSE........92 mg/dL
REF RANGE.....70-100

REMARKS: WNL

---------------------------
PHYSICIAN SIGNATURE
DR. SMITH
`}
                  </Box>
                </Box>

                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mt: "auto" }}>
                  {traditionalFeatures.map((feature, index) => (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: { xs: 1, sm: 1.5 },
                        mb: { xs: 1.25, sm: 1.5 },
                        color: "#7F7F7F",
                      }}
                    >
                      <X
                        size={18}
                        style={{
                          color: "#ef4444",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: { xs: "13px", sm: "14px", md: "15px" },
                          fontFamily: "'Poppins', sans-serif",
                          lineHeight: 1.5,
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default ComparisonSection;